import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { useCart } from '@/context/CartContext';
import { getCheckoutStatus } from '@/lib/api';
import SEO from '@/components/SEO';
import { CheckCircle, Loader2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CheckoutSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { clearCart } = useCart();
  const [status, setStatus] = useState('loading');
  const [paymentInfo, setPaymentInfo] = useState(null);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      const maxAttempts = 5;
      const pollInterval = 2000;

      const poll = async (attempt = 0) => {
        try {
          const response = await getCheckoutStatus(sessionId);
          const data = response.data;

          if (data.payment_status === 'paid') {
            setStatus('success');
            setPaymentInfo(data);
            clearCart();
            return;
          } else if (data.status === 'expired') {
            setStatus('expired');
            return;
          }

          if (attempt < maxAttempts) {
            setTimeout(() => poll(attempt + 1), pollInterval);
          } else {
            setStatus('pending');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          if (attempt < maxAttempts) {
            setTimeout(() => poll(attempt + 1), pollInterval);
          } else {
            setStatus('error');
          }
        }
      };

      poll();
    };

    checkPaymentStatus();
  }, [sessionId, clearCart]);

  return (
    <PublicLayout>
      <SEO
        title="Commande Confirmée — Merci pour votre achat"
        description="Votre commande Villa Marceau a été reçue. Vous recevrez un email de confirmation sous peu."
        canonical="/checkout/success"
        noindex={true}
      />
      <div className="py-20 px-6 lg:px-12">
        <div className="max-w-2xl mx-auto text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-3xl font-semibold mb-4">
                Vérification du paiement...
              </h1>
              <p className="text-muted-foreground">
                Merci de patienter pendant que nous confirmons votre paiement.
              </p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="font-heading text-3xl font-semibold mb-4" data-testid="success-title">
                Commande confirmée !
              </h1>
              <p className="text-muted-foreground mb-8">
                Merci pour votre achat ! Vous recevrez bientôt un email de confirmation 
                avec les détails de votre commande.
              </p>
              
              {paymentInfo && (
                <div className="bg-muted/50 rounded-lg p-6 mb-8 text-left">
                  <h3 className="font-heading text-lg font-semibold mb-4 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Détails de la commande
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Montant</span>
                      <span className="font-medium">
                        {(paymentInfo.amount_total / 100).toFixed(2)} €
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Statut</span>
                      <span className="text-green-600 font-medium">Payé</span>
                    </div>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/" data-testid="back-home-btn">
                  <Button variant="outline" className="w-full sm:w-auto">
                    Retour à l'accueil
                  </Button>
                </Link>
                <Link to="/commander-en-ligne" data-testid="continue-shopping-btn">
                  <Button className="btn-primary w-full sm:w-auto">
                    Continuer mes achats
                  </Button>
                </Link>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader2 className="w-16 h-16 text-amber-500 mx-auto mb-6 animate-spin" />
              <h1 className="font-heading text-3xl font-semibold mb-4">
                Paiement en cours de traitement
              </h1>
              <p className="text-muted-foreground mb-8">
                Votre paiement est en cours de vérification. 
                Vous recevrez un email de confirmation dès qu'il sera validé.
              </p>
              <Link to="/">
                <Button variant="outline">Retour à l'accueil</Button>
              </Link>
            </>
          )}

          {(status === 'error' || status === 'expired') && (
            <>
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-4xl">❌</span>
              </div>
              <h1 className="font-heading text-3xl font-semibold mb-4">
                {status === 'expired' ? 'Session expirée' : 'Erreur de paiement'}
              </h1>
              <p className="text-muted-foreground mb-8">
                {status === 'expired' 
                  ? 'Votre session de paiement a expiré. Veuillez réessayer.'
                  : 'Une erreur est survenue lors de la vérification du paiement.'}
              </p>
              <Link to="/panier">
                <Button className="btn-primary">Retourner au panier</Button>
              </Link>
            </>
          )}
        </div>
      </div>
    </PublicLayout>
  );
}
